import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import { Context } from "../../Reducers/Store";
import { formatarData, formatarDuracao, statusConfig } from "../../utils/deployStatus";
import { isOwnDeploy, canViewAllDeployments } from "../../utils/deployPermissions";

const FILTROS = [
  { key: "todos", label: "Todos" },
  { key: "sucesso", label: "Sucesso" },
  { key: "falha", label: "Falha" },
];

function matchesFiltro(deploy, filtro) {
  if (filtro === "sucesso") return deploy.status === "Succeeded";
  if (filtro === "falha") return deploy.status === "Failed";
  return true;
}

function applyEscopo(deploys, escopo, profile) {
  if (escopo !== "outros") return deploys;
  return deploys.filter((d) => !isOwnDeploy(d, profile));
}

function resolvePath(escopo) {
  return escopo === "meus" ? "/deployment-history/me" : "/deployment-history";
}

export default function DeployList() {
  const navigate = useNavigate();
  const [state] = useContext(Context);
  const canViewAll = canViewAllDeployments(state.profile);

  const [deploys, setDeploys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todos");
  const [escopo, setEscopo] = useState(canViewAll ? "todos" : "meus");

  useEffect(() => {
    const path = resolvePath(escopo);

    let cancelled = false;
    const fetchDeploys = (silent = false) => {
      const config = silent ? { metadata: { ignoreLoading: true } } : undefined;
      axios
        .get(`${process.env.REACT_APP_API}${path}`, config)
        .then((res) => {
          if (!cancelled) setDeploys(applyEscopo(res.data || [], escopo, state.profile));
        })
        .catch((err) => {
          if (cancelled) return;
          console.error(err);
          if (!silent) AlertComponent("Erro", "Erro ao carregar deploys", { icon: "error" });
        })
        .finally(() => {
          if (!cancelled && !silent) setLoading(false);
        });
    };

    setLoading(true);
    fetchDeploys(false);

    return () => { cancelled = true; };
  }, [escopo, state.profile]);

  const temDeployAtivo = deploys.some(
    (d) => d.lastStepStatus === "Running" || d.lastStepStatus === "Pending"
  );

  useEffect(() => {
    if (!temDeployAtivo) return;
    const path = resolvePath(escopo);
    const interval = setInterval(() => {
      axios
        .get(`${process.env.REACT_APP_API}${path}`, { metadata: { ignoreLoading: true } })
        .then((res) => setDeploys(applyEscopo(res.data || [], escopo, state.profile)))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [temDeployAtivo, escopo, state.profile]);

  const deploysFiltrados = deploys.filter((d) => matchesFiltro(d, filtro));

  const contagem = {
    todos: deploys.length,
    sucesso: deploys.filter((d) => d.status === "Succeeded").length,
    falha: deploys.filter((d) => d.status === "Failed").length,
  };

  return (
    <div className="max-w-6xl mx-auto">

      {/* Header */}
      <div
        className="rounded-2xl p-6 sm:p-8 mb-6 relative overflow-hidden text-white"
        style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #3b0764 100%)" }}
      >
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }} />
        <div className="absolute -bottom-12 -left-6 w-40 h-40 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#c4b5fd,transparent)" }} />
        <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <p className="text-purple-300 text-sm font-medium mb-1">Histórico</p>
            <h1 className="text-2xl sm:text-3xl font-bold">
              {escopo === "todos"
                ? "Todos os Deploys"
                : escopo === "outros"
                  ? "Deploys de Outros"
                  : "Meus Deploys"}
            </h1>
            <p className="text-purple-300 text-sm mt-1">
              {loading
                ? "Carregando..."
                : `${contagem.todos} deploy${contagem.todos !== 1 ? "s" : ""} registrado${contagem.todos !== 1 ? "s" : ""}`}
            </p>
          </div>
          <button
            onClick={() => navigate("/deploys/novo")}
            className="self-start sm:self-auto flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl text-indigo-900 bg-white hover:bg-purple-50 transition shadow-lg shadow-purple-900/30 shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Novo Deploy
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit">
          {FILTROS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFiltro(f.key)}
              className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${
                filtro === f.key
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {f.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                filtro === f.key ? "bg-violet-100 text-violet-700" : "bg-slate-200 text-slate-500"
              }`}>
                {loading ? "—" : contagem[f.key]}
              </span>
            </button>
          ))}
        </div>

        {canViewAll && (
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit">
            {[
              { key: "todos", label: "Todos" },
              { key: "meus", label: "Meus" },
              { key: "outros", label: "Outros" },
            ].map((o) => (
              <button
                key={o.key}
                onClick={() => setEscopo(o.key)}
                className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                  escopo === o.key
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
          </div>
        ) : deploysFiltrados.length === 0 ? (
          <div className="text-center py-20 px-6">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0l-4 4m4-4l4 4" />
              </svg>
            </div>
            <p className="text-slate-600 font-semibold mb-1">Nenhum deploy encontrado</p>
            <p className="text-slate-400 text-sm mb-5">
              {filtro === "todos"
                ? "Inicie seu primeiro deploy para começar a acompanhar o histórico."
                : "Nenhum resultado para este filtro."}
            </p>
            {filtro === "todos" && (
              <button
                onClick={() => navigate("/deploys/novo")}
                className="px-5 py-2 text-sm font-semibold rounded-xl text-white transition"
                style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}
              >
                Novo deploy
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Cards — mobile */}
            <div className="md:hidden divide-y divide-slate-100">
              {deploysFiltrados.map((deploy) => {
                const st = statusConfig(deploy.lastStepStatus || deploy.status);
                const target = deploy.target;
                const ambiente = target?.displayName || target?.siteName || "—";
                const autor = deploy.requestedBy?.displayName || deploy.requestedBy?.userName || "—";
                const observation = deploy.observation?.trim();
                return (
                  <div
                    key={deploy.id}
                    onClick={() => navigate(`/deploys/${deploy.id}`)}
                    className="flex items-start gap-3 p-4 hover:bg-slate-50 cursor-pointer transition-colors group"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${st.iconBg} ${st.iconColor}`}>
                      {st.icon}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-bold text-slate-900 text-sm truncate group-hover:text-violet-700 transition-colors">
                          {ambiente}
                        </p>
                        {target?.isProduction && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-rose-100 text-rose-700 ring-1 ring-rose-200">
                            Prod
                          </span>
                        )}
                        {deploy.restoreDatabaseRequested && (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-violet-100 text-violet-700 ring-1 ring-violet-200">
                            BD restaurado
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 min-w-0">
                        <span className="flex items-center gap-1 font-mono text-slate-600 truncate">
                          <svg className="w-3 h-3 shrink-0 text-slate-400" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <circle cx="6" cy="3" r="2" />
                            <circle cx="6" cy="21" r="2" />
                            <circle cx="18" cy="8" r="2" />
                            <path d="M6 5v14" />
                            <path d="M18 10v2a4 4 0 0 1-4 4H9" />
                          </svg>
                          <span className="truncate">{deploy.branchName}</span>
                        </span>
                        <span className="text-slate-300">·</span>
                        <span className="flex items-center gap-1 truncate">
                          <svg className="w-3 h-3 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="truncate">{autor}</span>
                        </span>
                      </div>
                      {observation && (
                        <p className="mt-1 text-xs text-slate-500 truncate">
                          {observation}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold rounded-full ${st.badgeClass}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dotClass}`} />
                          {st.label}
                        </span>
                        <span className="text-xs text-slate-400 tabular-nums">{formatarDuracao(deploy.duration)}</span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {formatarData(deploy.finishedAt || deploy.startedAt || deploy.dataAgendamento || deploy.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tabela — desktop */}
            <div className="hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Ambiente</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Branch</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Autor</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Iniciado em</th>
                    <th className="px-4 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {deploysFiltrados.map((deploy) => {
                    const st = statusConfig(deploy.lastStepStatus || deploy.status);
                    const target = deploy.target;
                    const ambiente = target?.displayName || target?.siteName || "—";
                    const autor = deploy.requestedBy?.displayName || deploy.requestedBy?.userName || "—";
                    const observation = deploy.observation?.trim();

                    return (
                      <tr
                        key={deploy.id}
                        className="hover:bg-slate-50 cursor-pointer transition-colors group"
                        onClick={() => navigate(`/deploys/${deploy.id}`)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${st.iconBg} ${st.iconColor}`}>
                              {st.icon}
                            </div>
                            <div className="min-w-0 flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-slate-900 text-sm truncate group-hover:text-violet-700 transition-colors">
                                {ambiente}
                              </span>
                              {target?.isProduction && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-rose-100 text-rose-700 ring-1 ring-rose-200 shrink-0">
                                  Prod
                                </span>
                              )}
                              {deploy.restoreDatabaseRequested && (
                                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-violet-100 text-violet-700 ring-1 ring-violet-200 shrink-0">
                                  BD restaurado
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="min-w-0 max-w-xs">
                            <span className="inline-flex items-center gap-1.5 text-sm font-mono text-slate-600 min-w-0 max-w-full">
                              <svg className="w-3.5 h-3.5 shrink-0 text-slate-400" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <circle cx="6" cy="3" r="2" />
                                <circle cx="6" cy="21" r="2" />
                                <circle cx="18" cy="8" r="2" />
                                <path d="M6 5v14" />
                                <path d="M18 10v2a4 4 0 0 1-4 4H9" />
                              </svg>
                              <span className="truncate">{deploy.branchName}</span>
                            </span>
                            {observation && (
                              <p className="mt-1 text-xs text-slate-500 truncate">
                                {observation}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">{autor}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-500">
                            {formatarData(deploy.startedAt || deploy.dataAgendamento || deploy.createdAt)}
                          </div>
                          {deploy.duration && (
                            <div className="text-[11px] text-slate-400 tabular-nums mt-0.5">
                              {formatarDuracao(deploy.duration)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full ${st.badgeClass}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${st.dotClass}`} />
                            {st.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
